import { Link, useLocation } from "react-router-dom";
import { FaCogs, FaTools } from "react-icons/fa";
import HeaderDesktopView from "./HeaderDesktopView";
import HeaderMobileView from "./HeaderMobileView";
import HeaderSearch from "./HeaderSearch";
import { CORPORATE_SITE, TOOLS_SITE } from "../../constant/HeaderData";
import "../../variables.css";

// Horizontal lockup derived from the QuantaRock logo pack, which only ships
// the stacked mark — at the 32-44px this renders at, a stacked lockup's
// wordmark would be a few pixels tall, so the icon sits beside it instead.
// .webp, palette-quantised: the artwork's soft glow costs ~20KB as lossy
// WebP and 7KB this way, with no visible difference at this size.
//
// Served from public/ by URL rather than imported. As an import it was 7KB
// under CRA's 10KB inline threshold, so it was base64'd into main.js — which
// meant the logo could not appear until the whole bundle had downloaded and
// run. The boot shell in index.html paints this same URL during HTML parse, so
// by the time React mounts the file is already in cache and this <img> is
// instant.
const headerLogo = "/assets/weblogo.webp";

const Header = () => {
  const { pathname } = useLocation();
  // The homepage hero carries its own large search with suggestions; a second
  // box in the chrome directly above it is just noise.
  const showSearch = pathname !== "/";

  return (
    <header className="sticky top-0 z-50 bg-[var(--website-primary-color)] shadow-md">
      {/* Three zones: the outer two are `flex-1 basis-0`, so they take equal
          width whatever they hold and the nav between them lands on the true
          centre of the bar rather than drifting with the logo's width. */}
      <div className="mx-auto flex h-16 max-w-[88rem] items-center gap-3 px-3 sm:h-20 sm:gap-4 sm:px-6">
        <div className="flex min-w-0 flex-1 basis-0 items-center gap-3 sm:gap-4">
          <HeaderMobileView />

          <Link to="/" aria-label="Interseller home" className="shrink-0">
            <img
              src={headerLogo}
              alt="Interseller"
              width="661"
              height="112"
              className="h-8 w-auto sm:h-11"
            />
          </Link>
        </div>

        <HeaderDesktopView />

        <div className="flex flex-1 basis-0 items-center justify-end gap-2">
          {/* Brand mint keeps Tools distinct from the white QuantaRock button
              beside it — two solid white buttons would read as one block. The
              two swap fills on hover: mint→white here, white→mint there. */}
          <a
            href={TOOLS_SITE.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={TOOLS_SITE.label}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-white
                       transition-colors hover:bg-white/10 sm:hidden"
          >
            <FaTools size={18} />
          </a>

          <a
            href={TOOLS_SITE.url}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-lg bg-[var(--website-secondary-color)] px-3 py-2
                       text-xs font-bold text-[var(--website-primary-color)]
                       transition-colors hover:bg-white sm:block lg:text-[0.8rem]"
          >
            {TOOLS_SITE.label}
          </a>

          <a
            href={CORPORATE_SITE.servicesUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Visit ${CORPORATE_SITE.label}`}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-white
                       transition-colors hover:bg-white/10 sm:hidden"
          >
            <FaCogs size={20} />
          </a>

          <a
            href={CORPORATE_SITE.servicesUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-lg bg-white px-3 py-2 text-xs font-bold
                       text-[var(--website-primary-color)] transition-colors
                       hover:bg-[var(--website-secondary-color)] sm:block lg:text-[0.8rem]"
          >
            {CORPORATE_SITE.label}
          </a>
        </div>
      </div>

      {/* Search gets its own row at every size. In the bar it was competing with
          the centred nav for the same middle space and lost — down to ~150px on
          a 1400px screen — which is unusable on a site people shop by keyword. */}
      {showSearch && (
        <div className="border-t border-white/10 px-3 py-2 sm:px-6 sm:pb-2.5">
          <HeaderSearch className="mx-auto w-full max-w-2xl" />
        </div>
      )}
    </header>
  );
};

export default Header;
